import React from 'react';
import horse from './watchDetail/WatchLive/horse-new.png';
import { TokenStats } from '../../types/race';
import dayjs from 'dayjs';

interface RaceTracksProps {
  tokens: TokenStats[];
  raceStartTime: bigint;
  raceEndTime: bigint;
  trackClassName: string;
  horseImageClassName: string;
  tokenClassName: string;
  tokenIconClassName: string;
  tokenSymbolClassName: string;
  horsePosition: { first: string; rest: string };
  compact?: boolean;
}

// Helper function to determine track gradient based on symbol
const getTrackGradient = (symbol: string): string => {
  const symbolLower = symbol.toLowerCase();

  // Map of gradients by symbol
  const gradients: Record<string, { from: string; to: string }> = {
    btc: { from: 'rgba(182, 110, 23, 0)', to: '#B66E17' },
    eth: { from: 'rgba(59, 130, 246, 0)', to: '#3B82F6' },
    xrp: { from: 'rgba(107, 114, 128, 0)', to: '#6B7280' },
    sol: { from: 'rgba(126, 34, 206, 0)', to: '#7E22CE' },
    link: { from: 'rgba(37, 99, 235, 0)', to: '#2563EB' },
    default: { from: 'rgba(75, 85, 99, 0)', to: '#4B5563' },
  };

  const gradient = gradients[symbolLower] || gradients.default;

  return `linear-gradient(90deg, ${gradient.from} 0%, ${gradient.to} 100%)`;
};

// Helper function to calculate track width based on percentChange
const calculateTrackWidth = (
  percentChange: number,
  maxPercentChange: number,
  isRaceStarted: boolean,
  isRaceEnded: boolean,
  compact: boolean
): number => {
  if (!isRaceStarted) {
    return compact ? 10 : 16; // All horses at start line
  }
  if (isRaceEnded) {
    return compact ? 92 : 98; // All horses at finish line
  }

  // Normalize the percentage change to a scale of 0-50
  const normalizedChange = ((percentChange - -maxPercentChange) / (maxPercentChange - -maxPercentChange)) * 50;

  return compact
    ? Math.min(Math.max(normalizedChange + 24, 10), 92)
    : Math.min(Math.max(normalizedChange + 24, 10), 98);
};

const RaceTracks: React.FC<RaceTracksProps> = ({
  tokens = [],
  raceStartTime,
  raceEndTime,
  trackClassName,
  horseImageClassName,
  tokenClassName,
  tokenIconClassName,
  tokenSymbolClassName,
  horsePosition,
  compact = true,
}: RaceTracksProps) => {
  // Check if race has ended
  const isRaceStarted = BigInt(dayjs().unix()) >= raceStartTime;
  const isRaceEnded = BigInt(dayjs().unix()) >= raceEndTime;

  // Find the maximum absolute percentage change
  const maxPercentChange = Math.max(...tokens.map(token => Math.abs(token.token.score)));

  return (
    <div className="h-full w-full">
      {/* Finish Line */}
      <div className={`absolute h-full ${trackClassName}`}>
        <div className={`grid h-full w-full grid-cols-2`}>
          {[...Array(102)].map((_, i) => (
            <div
              key={i}
              className={`${i % 2 === 0 ? (Math.floor(i / 2) % 2 === 0 ? 'bg-black' : 'bg-white') : Math.floor(i / 2) % 2 === 0 ? 'bg-white' : 'bg-black'}`}
            />
          ))}
        </div>
      </div>

      {/* Render tracks dynamically */}
      {tokens.map((token, index) => {
        const percentChange = token.token.score;
        const trackWidth = calculateTrackWidth(percentChange, maxPercentChange, isRaceStarted, isRaceEnded, compact);

        return (
          <div
            key={token.token.symbol}
            className={`relative ${index === 0 ? horsePosition.first : horsePosition.rest}`}
          >
            <div className={`rounded-full ${compact ? 'h-2' : 'h-4'}`}>
              <div
                className="relative h-full rounded-full"
                style={{
                  width: `${trackWidth}%`,
                  background: getTrackGradient(token.token.symbol),
                }}
              >
                {/* Horse positioned at the end of the colored track */}
                <div className={`absolute -right-4 translate-x-0 transform ${compact ? '-top-7' : '-top-16'}`}>
                  <img src={horse} alt="Horse" className={horseImageClassName} />
                </div>

                {/* Token icon, symbol and price change positioned below the horse */}
                <div className={`absolute translate-x-1/2 transform ${tokenClassName}`}>
                  <div className="flex items-center gap-1 whitespace-nowrap">
                    <div
                      className={`flex items-center justify-center overflow-hidden rounded-full bg-white ${tokenIconClassName}`}
                    >
                      <img src={token.token.marketData?.image} alt={`${token.token.symbol}`} />
                    </div>
                    <span className={`text-white ${tokenSymbolClassName}`}>{token.token.symbol}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RaceTracks;
